"use client";
import { useState, useMemo } from "react";
import Head from "next/head";
import Link from "next/link";
import Image from "next/image";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  FaArrowRight,
  FaCalendar,
  FaClock,
  FaEnvelope,
  FaGraduationCap,
  FaLocationDot,
  FaMagnifyingGlass,
  FaUser,
  FaUserGraduate,
  FaUserGroup,
  FaVideo,
  FaLinkedin,
  FaXTwitter,
  FaGithub,
  FaChevronDown,
  FaStar,
  FaHeart,
  FaLightbulb,
  FaRocket,
  FaUsers,
  FaBriefcase,
} from "react-icons/fa6";
import { FaHandsHelping, FaSearch } from "react-icons/fa";
import api from "@/lib/axiosInstance";
import {
  AlumniGridSkeleton,
  MentorshipProgramSkeleton,
} from "@/components/ui/Skeletons";

interface AlumniProfile {
  _id: string;
  userId: {
    _id: string;
    fullName: string;
    email: string;
    avatar?: string;
  };
  graduationYear: number;
  specialization: string;
  currentRole: string;
  bio?: string;
  skills?: string[];
  education?: Array<{
    schoolName: string;
    course: string;
  }>;
  availableForMentorship: boolean;
  isMentor: boolean;
  socialLinks?: {
    linkedin?: string;
    twitter?: string;
    github?: string;
  };
}

interface MentorshipProgram {
  _id: string;
  title: string;
  description: string;
  mentorId: {
    userId: {
      fullName: string;
    };
  };
  duration: string;
  timeCommitment: string;
  maxParticipants: number;
  currentParticipants: number;
  applicationDeadline?: string;
  programStartDate?: string;
}

const AlumniPage = () => {
  const [activeTab, setActiveTab] = useState("network");
  const [selectedAlumni, setSelectedAlumni] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [specializationFilter, setSpecializationFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");

  // Fetch alumni profiles
  const {
    data: alumni = [],
    isLoading: alumniLoading,
    error: alumniError,
  } = useQuery({
    queryKey: ["alumni"],
    queryFn: async () => {
      const response = await api.get("/alumni");
      return response.data.alumni as AlumniProfile[];
    },
    staleTime: 1000 * 60 * 5,
  });

  // Fetch mentorship programs
  const { data: mentorshipPrograms = [], isLoading: programsLoading } =
    useQuery({
      queryKey: ["mentorship-programs"],
      queryFn: async () => {
        const response = await api.get("/mentorship-programs");
        return response.data.programs as MentorshipProgram[];
      },
      staleTime: 1000 * 60 * 5,
    });

  // Filter alumni based on search and filters
  const filteredAlumni = useMemo(() => {
    return alumni.filter((person) => {
      const matchesSearch =
        !searchTerm ||
        person.userId.fullName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        person.currentRole.toLowerCase().includes(searchTerm.toLowerCase()) ||
        person.specialization.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesSpecialization =
        !specializationFilter ||
        person.specialization
          .toLowerCase()
          .includes(specializationFilter.toLowerCase());

      const matchesLocation =
        !locationFilter ||
        person.education?.some((edu) =>
          edu.schoolName.toLowerCase().includes(locationFilter.toLowerCase())
        );

      return matchesSearch && matchesSpecialization && matchesLocation;
    });
  }, [alumni, searchTerm, specializationFilter, locationFilter]);

  // Get unique specializations for filters
  const specializations = useMemo(() => {
    return Array.from(
      new Set(alumni.map((person) => person.specialization))
    ).filter(Boolean);
  }, [alumni]);

  // Get available mentorship programs
  const availableMentorshipPrograms = mentorshipPrograms.filter(
    (program) =>
      program.currentParticipants < program.maxParticipants &&
      (!program.applicationDeadline ||
        new Date() <= new Date(program.applicationDeadline))
  );

  const handleContactAlumni = (email: string, name: string) => {
    const subject = encodeURIComponent(
      `CONCES - Connection Request from ${name}`
    );
    const body = encodeURIComponent(
      `Hello,\n\nI found your profile on the CONCES Alumni Network and would love to connect.\n\nBest regards`
    );
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, "_blank");
  };

  if (alumniError) {
    toast.error("Failed to load alumni data. Please try again later.");
  }

  // Stats for the hero section
  const stats = [
    { label: "Active Alumni", value: alumni.length || "500+", icon: FaUsers },
    {
      label: "Available Mentors",
      value: alumni.filter((a) => a.availableForMentorship).length || "50+",
      icon: FaUserGraduate,
    },
    {
      label: "Mentorship Programs",
      value: mentorshipPrograms.length || "20+",
      icon: FaLightbulb,
    },
    { label: "Success Stories", value: "100+", icon: FaStar },
  ];

  const renderNetworkContent = () => {
    if (alumniLoading) {
      return <AlumniGridSkeleton count={9} />;
    }

    if (filteredAlumni.length === 0) {
      return (
        <div className="text-center py-16 bg-gradient-to-br from-primary-50 to-secondary-50 rounded-3xl">
          <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaUserGroup className="text-4xl text-primary-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            {alumni.length === 0
              ? "No Alumni Profiles Yet"
              : "No Results Found"}
          </h3>
          <p className="text-gray-600 max-w-md mx-auto mb-6">
            {alumni.length === 0
              ? "Be the first to join our growing alumni network and connect with fellow CONCES members."
              : "Try adjusting your search criteria to discover more alumni in our network."}
          </p>
          {alumni.length === 0 && (
            <Link
              href="/alumni/join"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg"
            >
              Join Alumni Network
              <FaArrowRight className="ml-2" />
            </Link>
          )}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAlumni.map((person, index) => (
          <div
            key={person._id}
            className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-primary-200"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            {/* Card Header with Gradient */}
            <div className="relative h-24 bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-500">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
              </div>
              {person.availableForMentorship && (
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full border border-white/30">
                    ✨ Open to Mentor
                  </span>
                </div>
              )}
            </div>

            {/* Profile Image */}
            <div className="relative -mt-12 px-6">
              <div className="relative">
                <Image
                  src={person.userId.avatar || "/default-avatar.png"}
                  alt={person.userId.fullName}
                  width={80}
                  height={80}
                  className="w-20 h-20 rounded-2xl object-cover border-4 border-white shadow-lg"
                />
                {person.isMentor && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-secondary-500 rounded-full flex items-center justify-center border-2 border-white">
                    <FaStar className="text-white text-xs" />
                  </div>
                )}
              </div>
            </div>

            {/* Card Content */}
            <div className="p-6 pt-4">
              <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-primary-600 transition-colors">
                {person.userId.fullName}
              </h3>
              <p className="text-sm text-primary-600 font-medium mb-3">
                {person.currentRole}
              </p>

              <div className="space-y-2 mb-4">
                <p className="flex items-center text-sm text-gray-600">
                  <FaGraduationCap className="mr-2 text-secondary-500" />
                  Class of {person.graduationYear} • {person.specialization}
                </p>
                {person.education && person.education[0] && (
                  <p className="flex items-center text-sm text-gray-600">
                    <FaLocationDot className="mr-2 text-secondary-500" />
                    {person.education[0].schoolName}
                  </p>
                )}
              </div>

              {person.bio && (
                <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                  {person.bio}
                </p>
              )}

              {/* Skills Tags */}
              {person.skills && person.skills.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {person.skills.slice(0, 3).map((skill, idx) => (
                    <span
                      key={idx}
                      className="px-2.5 py-1 bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 text-xs font-medium rounded-lg"
                    >
                      {skill}
                    </span>
                  ))}
                  {person.skills.length > 3 && (
                    <span className="px-2.5 py-1 text-gray-500 text-xs">
                      +{person.skills.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Social Links */}
              {person.socialLinks && (
                <div className="flex items-center gap-2 mb-4">
                  {person.socialLinks.linkedin && (
                    <a
                      href={person.socialLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 bg-gray-100 hover:bg-primary-100 rounded-lg flex items-center justify-center transition-colors"
                    >
                      <FaLinkedin className="text-gray-600 hover:text-primary-600" />
                    </a>
                  )}
                  {person.socialLinks.twitter && (
                    <a
                      href={person.socialLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 bg-gray-100 hover:bg-primary-100 rounded-lg flex items-center justify-center transition-colors"
                    >
                      <FaXTwitter className="text-gray-600 hover:text-primary-600" />
                    </a>
                  )}
                  {person.socialLinks.github && (
                    <a
                      href={person.socialLinks.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 bg-gray-100 hover:bg-primary-100 rounded-lg flex items-center justify-center transition-colors"
                    >
                      <FaGithub className="text-gray-600 hover:text-primary-600" />
                    </a>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                <button
                  onClick={() =>
                    handleContactAlumni(
                      person.userId.email,
                      person.userId.fullName
                    )
                  }
                  className="flex-1 flex items-center justify-center px-4 py-2.5 bg-primary-50 text-primary-700 text-sm font-semibold rounded-xl hover:bg-primary-100 transition-colors"
                >
                  <FaEnvelope className="mr-2" />
                  Connect
                </button>
                <button
                  onClick={() => setSelectedAlumni(person._id)}
                  className="flex-1 flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-sm font-semibold rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all"
                >
                  View Profile
                  <FaArrowRight className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMentorshipContent = () => {
    return (
      <div className="space-y-8">
        {/* Mentorship Intro Card */}
        <div className="relative overflow-hidden bg-gradient-to-r from-primary-700 via-primary-800 to-primary-900 rounded-3xl p-8 md:p-12 text-white">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-secondary-400 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          </div>

          <div className="relative z-10 max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6 border border-white/30">
              <FaHeart className="mr-2 text-secondary-400" />
              Empowering the Next Generation
            </div>

            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              CONCES Alumni Mentorship Program
            </h2>
            <p className="text-lg text-white/90 leading-relaxed mb-8">
              Connect with experienced alumni who are passionate about guiding
              the next generation of Christian engineers. Our mentors provide
              career advice, spiritual guidance, and professional development
              support.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/user/mentorship"
                className="inline-flex items-center justify-center px-6 py-3.5 bg-secondary-500 text-primary-900 font-bold rounded-xl hover:bg-secondary-400 transition-all shadow-lg"
              >
                <FaUserGraduate className="mr-2" />
                Become a Mentor
              </Link>
              <Link
                href="/user/network"
                className="inline-flex items-center justify-center px-6 py-3.5 bg-white/20 backdrop-blur-sm text-white font-semibold rounded-xl hover:bg-white/30 transition-all border border-white/30"
              >
                <FaSearch className="mr-2" />
                Find a Mentor
              </Link>
            </div>
          </div>
        </div>

        {/* Why Mentorship Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-2xl border border-primary-100">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
              <FaLightbulb className="text-primary-600 text-xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Career Guidance
            </h3>
            <p className="text-gray-600 text-sm">
              Get personalized advice on career paths, industry trends, and
              professional development strategies.
            </p>
          </div>
          <div className="bg-gradient-to-br from-secondary-50 to-white p-6 rounded-2xl border border-secondary-100">
            <div className="w-12 h-12 bg-secondary-100 rounded-xl flex items-center justify-center mb-4">
              <FaHeart className="text-secondary-600 text-xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Spiritual Support
            </h3>
            <p className="text-gray-600 text-sm">
              Navigate the intersection of faith and engineering with guidance
              from experienced Christian professionals.
            </p>
          </div>
          <div className="bg-gradient-to-br from-accent-100 to-white p-6 rounded-2xl border border-accent-200">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
              <FaRocket className="text-primary-600 text-xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Skill Development
            </h3>
            <p className="text-gray-600 text-sm">
              Enhance your technical and soft skills through hands-on mentorship
              and real-world insights.
            </p>
          </div>
        </div>

        {/* Available Programs */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                  Available Mentorship Programs
                </h2>
                <p className="text-gray-600">
                  Apply to join a structured mentorship program
                </p>
              </div>
              <span className="px-4 py-2 bg-primary-100 text-primary-700 text-sm font-semibold rounded-full">
                {availableMentorshipPrograms.length} Open
              </span>
            </div>
          </div>

          <div className="p-6 md:p-8">
            {programsLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <MentorshipProgramSkeleton key={index} />
                ))}
              </div>
            ) : availableMentorshipPrograms.length > 0 ? (
              <div className="space-y-4">
                {availableMentorshipPrograms.map((program) => (
                  <div
                    key={program._id}
                    className="group p-6 bg-gradient-to-r from-gray-50 to-white rounded-2xl border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                          {program.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {program.description}
                        </p>

                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center">
                            <FaUser className="mr-2 text-primary-500" />
                            {program.mentorId.userId.fullName}
                          </span>
                          <span className="flex items-center">
                            <FaClock className="mr-2 text-secondary-500" />
                            {program.duration}
                          </span>
                          <span className="flex items-center">
                            <FaUserGroup className="mr-2 text-primary-500" />
                            {program.currentParticipants}/
                            {program.maxParticipants} spots
                          </span>
                          {program.applicationDeadline && (
                            <span className="flex items-center text-secondary-600">
                              <FaCalendar className="mr-2" />
                              Deadline:{" "}
                              {new Date(
                                program.applicationDeadline
                              ).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <Link
                          href={`/user/mentorship/program/${program._id}`}
                          className="px-4 py-2.5 border-2 border-primary-200 text-primary-700 text-sm font-semibold rounded-xl hover:bg-primary-50 transition-colors"
                        >
                          Learn More
                        </Link>
                        <button className="px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-sm font-semibold rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all shadow-md">
                          Apply Now
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaUserGraduate className="text-3xl text-primary-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  No Programs Available
                </h3>
                <p className="text-gray-600 max-w-md mx-auto mb-6">
                  There are currently no open mentorship programs. Check back
                  later or find individual mentors.
                </p>
                <Link
                  href="/user/network"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg"
                >
                  <FaSearch className="mr-2" />
                  Find Individual Mentors
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTalksContent = () => {
    return (
      <div className="space-y-8">
        {/* Upcoming Talks */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 md:p-8 border-b border-gray-100 bg-gradient-to-r from-primary-50 to-secondary-50">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              Upcoming Alumni Talks
            </h2>
            <p className="text-gray-600">
              Learn from industry experts and fellow alumni
            </p>
          </div>

          <div className="p-6 md:p-8">
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaVideo className="text-3xl text-primary-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Coming Soon
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                New talks are being scheduled. Stay tuned for upcoming sessions
                with our distinguished alumni.
              </p>
            </div>
          </div>
        </div>

        {/* Past Talks Archive */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 md:p-8 border-b border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              Past Talks Archive
            </h2>
            <p className="text-gray-600">
              Watch recordings of previous alumni sessions
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Topic
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Speaker
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Recording
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900">
                    Ethics in Engineering Practice
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    Dr. Adebayo Ogunlesi
                  </td>
                  <td className="px-6 py-4 text-gray-600">March 15, 2025</td>
                  <td className="px-6 py-4">
                    <a
                      href="#"
                      className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
                    >
                      <FaVideo className="mr-2" />
                      Watch
                    </a>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900">
                    Women in Engineering Leadership
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    Engr. Fatima Abdullahi
                  </td>
                  <td className="px-6 py-4 text-gray-600">February 28, 2025</td>
                  <td className="px-6 py-4">
                    <a
                      href="#"
                      className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
                    >
                      <FaVideo className="mr-2" />
                      Watch
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="p-6 border-t border-gray-100 text-center">
            <button className="px-6 py-2.5 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors">
              View All Past Talks
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "network":
        return renderNetworkContent();
      case "mentorship":
        return renderMentorshipContent();
      case "talks":
        return renderTalksContent();
      default:
        return null;
    }
  };

  return (
    <>
      <Head>
        <title>Alumni Network | CONCES</title>
        <meta
          name="description"
          content="Connect with CONCES alumni for networking, mentorship, and professional development"
        />
      </Head>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-800 via-primary-900 to-primary-950 pt-20 pb-32">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-secondary-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary-400/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          <div className="absolute top-1/2 left-1/2 w-[300px] h-[300px] bg-white/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
              backgroundSize: "50px 50px",
            }}
          ></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium text-white/90 mb-6 border border-white/20">
              <FaUsers className="mr-2 text-secondary-400" />
              Connecting Generations of Christian Engineers
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              CONCES{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary-400 to-secondary-300">
                Alumni Network
              </span>
            </h1>

            <p className="text-lg md:text-xl text-white/80 mb-8 leading-relaxed">
              Join a vibrant community of Christian engineers who are making an
              impact. Connect with mentors, share experiences, and grow together
              in faith and profession.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Link
                href="/alumni/join"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-secondary-500 to-secondary-400 text-primary-900 font-bold rounded-xl hover:from-secondary-400 hover:to-secondary-300 transition-all shadow-lg shadow-secondary-500/30"
              >
                <FaUserGraduate className="mr-2" />
                Join Alumni Network
              </Link>
              <button
                onClick={() => setActiveTab("mentorship")}
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl hover:bg-white/20 transition-all border border-white/20"
              >
                <FaHandsHelping className="mr-2" />
                Find a Mentor
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 text-center border border-white/10 hover:bg-white/15 transition-colors"
              >
                <stat.icon className="text-2xl text-secondary-400 mx-auto mb-3" />
                <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-white/70">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Wave Bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-auto"
          >
            <path
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 -mt-16 relative z-20">
        {/* Tab Navigation */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-2 mb-8 inline-flex">
          <button
            onClick={() => setActiveTab("network")}
            className={`flex items-center px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
              activeTab === "network"
                ? "bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <FaUserGroup className="mr-2" />
            Network
          </button>
          <button
            onClick={() => setActiveTab("mentorship")}
            className={`flex items-center px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
              activeTab === "mentorship"
                ? "bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <FaHandsHelping className="mr-2" />
            Mentorship
          </button>
          <button
            onClick={() => setActiveTab("talks")}
            className={`flex items-center px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
              activeTab === "talks"
                ? "bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <FaVideo className="mr-2" />
            Talks
          </button>
        </div>

        {/* Search and Filter */}
        {activeTab === "network" && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FaMagnifyingGlass className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all text-gray-900 placeholder-gray-500"
                  placeholder="Search alumni by name, role, or specialization..."
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative">
                  <select
                    value={specializationFilter}
                    onChange={(e) => setSpecializationFilter(e.target.value)}
                    className="appearance-none w-full sm:w-auto px-4 py-3 pr-10 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all text-gray-900 bg-white font-medium"
                  >
                    <option value="">All Specializations</option>
                    {specializations.map((spec) => (
                      <option key={spec} value={spec}>
                        {spec}
                      </option>
                    ))}
                  </select>
                  <FaChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>

                <div className="relative">
                  <select
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="appearance-none w-full sm:w-auto px-4 py-3 pr-10 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all text-gray-900 bg-white font-medium"
                  >
                    <option value="">All Institutions</option>
                    {Array.from(
                      new Set(
                        alumni.flatMap(
                          (person) =>
                            person.education?.map((edu) => edu.schoolName) || []
                        )
                      )
                    )
                      .filter(Boolean)
                      .map((institution) => (
                        <option key={institution} value={institution}>
                          {institution}
                        </option>
                      ))}
                  </select>
                  <FaChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Active Filters */}
            {(searchTerm || specializationFilter || locationFilter) && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                <span className="text-sm text-gray-600">Active filters:</span>
                {searchTerm && (
                  <span className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full">
                    "{searchTerm}"
                    <button
                      onClick={() => setSearchTerm("")}
                      className="ml-2 hover:text-primary-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                {specializationFilter && (
                  <span className="inline-flex items-center px-3 py-1 bg-secondary-100 text-secondary-700 text-sm font-medium rounded-full">
                    {specializationFilter}
                    <button
                      onClick={() => setSpecializationFilter("")}
                      className="ml-2 hover:text-secondary-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                {locationFilter && (
                  <span className="inline-flex items-center px-3 py-1 bg-accent-200 text-gray-700 text-sm font-medium rounded-full">
                    {locationFilter}
                    <button
                      onClick={() => setLocationFilter("")}
                      className="ml-2 hover:text-gray-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setSpecializationFilter("");
                    setLocationFilter("");
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium ml-2"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        {renderContent()}

        {/* CTA Section */}
        <section className="mt-16 bg-gradient-to-r from-primary-700 via-primary-800 to-primary-900 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-secondary-400 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10 max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Make an Impact?
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Join our alumni network today and be part of a community that's
              shaping the future of Christian engineering professionals.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/alumni/join"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-secondary-500 text-primary-900 font-bold rounded-xl hover:bg-secondary-400 transition-all shadow-lg"
              >
                Join Now
                <FaArrowRight className="ml-2" />
              </Link>
              <Link
                href="/contact"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl hover:bg-white/20 transition-all border border-white/20"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default AlumniPage;
